import { Box, BoxProps } from '@chakra-ui/react'
import { OnDatesChangeProps } from '@datepicker-react/hooks'
import { isMatch, parse } from 'date-fns'
import React, { useEffect, useRef, useState } from 'react'
import { usePopper } from 'react-popper'

import { IconCalendar } from '~shared-components/icons/brand/IconCalendar'

import { formatDate } from '../../utils/misc'
import { Helper } from '../Helper/Helper'
import { Input, InputProps } from '../Input/Input'
import { DatePicker } from './DatePicker'

export interface InputDatePickerProps
  extends Omit<InputProps, 'value' | 'unit' | 'showClearButton' | 'type'> {
  dateFormatter?: (date: Date) => string
  isDateBlocked?: (date: Date) => boolean
  onBlur?: () => void
  onDateChange?: (date: Date) => void
  onError?: () => void
  onFocus?: () => void
  showCalendarOnMount?: boolean
  value?: string | Date
}

// Add these to align options centered beneath the input
// Might be useful at some point
//  display='flex'
//  justifyContent= 'center'
//
interface DatePickerContainerProps extends BoxProps {
  show: boolean
}
const DatePickerContainer = React.forwardRef<
  HTMLDivElement,
  DatePickerContainerProps
>(({ show, ...props }, ref) => (
  <Box
    ref={ref}
    left={0}
    pointerEvents={show ? 'all' : 'none'}
    position="absolute"
    right={0}
    visibility={show ? 'visible' : 'hidden'}
    zIndex={1010}
    {...props}
  />
))
DatePickerContainer.displayName = 'DatePickerContainer'

const defaultDdateFormatter = (date: Date) => formatDate(date, '/')
export const InputDatePicker: React.FC<InputDatePickerProps> = ({
  error,
  dateFormatter = defaultDdateFormatter,
  rightAddon = <IconCalendar boxSize="20px" color="neutral.90" />,
  isDateBlocked,
  onBlur,
  onDateChange,
  onError,
  onFocus,
  showCalendarOnMount = false,
  value,
  helper,
  ...rest
}) => {
  const [date, setDate] = useState<Date | undefined>()
  const [manualChange, setManualChange] = useState<boolean>(false)
  const [dateError, setDateError] = useState<boolean>(false)
  const [dateInput, setDateInput] = useState<string>('')
  const [showCalendar, setShowCalendar] = useState(showCalendarOnMount)
  const ref = useRef<HTMLInputElement>(null)
  const popperRef = useRef<HTMLDivElement>(null)

  const { attributes, styles } = usePopper(ref.current, popperRef.current, {
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          altAxis: true,
          mainAxis: false,
        },
      },
    ],
    placement: 'bottom',
  })

  useEffect(() => {
    setDateInput(value ? dateFormatter(new Date(value)) : '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const closeCalendar = (ev: MouseEvent | TouchEvent) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
    // @ts-ignore - Don't know why typescript is complaining here
    if (ref.current && !ref.current.contains(ev.target)) {
      setShowCalendar(false)
    }
  }

  const openCalendar = () => {
    setShowCalendar(true)
  }

  useEffect(() => {
    document.addEventListener('click', closeCalendar, { capture: true })
    document.addEventListener('touchend', closeCalendar, { capture: true })

    return function onUnmount() {
      document.removeEventListener('click', closeCalendar, { capture: true })
      document.removeEventListener('touchend', closeCalendar, { capture: true })
    }
  }, [])

  const validateDate = (value: string) => {
    setDateInput(value)
    if (
      isMatch(value, 'dd-MM-yyyy') ||
      isMatch(value, 'dd.MM.yyyy') ||
      isMatch(value, 'dd/MM/yyyy')
    ) {
      // Get separator from input value
      const s = (value.match(/\d([.\-/])\d/) || [[undefined, undefined]])[1]
      if (s) {
        const asDate = parse(value, `dd${s}MM${s}yyyy`, new Date())
        setDate(asDate)
        setManualChange(true)
        setDateError(false)
      }
    } else {
      setDateError(true)
      setManualChange(false)
      onError && onError()
    }
  }

  const dateChangeHandler = (data: OnDatesChangeProps) => {
    setShowCalendar(false)
    if (data.startDate) {
      const formattedDate = dateFormatter(data.startDate)
      setDateInput(formattedDate)
      validateDate(formattedDate)
      onDateChange && onDateChange(data.startDate)
    }
  }

  const handleBlur = () => {
    if (date) {
      if (dateInput !== dateFormatter(date)) {
        setDateInput(dateFormatter(date))
      }

      // Ensure manual input only triggers onDateChange once
      if (manualChange && onDateChange) {
        onDateChange(date)
        setManualChange(false)
      }
    }
  }

  return (
    <Box
      ref={ref}
      data-testid="input-data-picker"
      onBlur={onBlur}
      onFocus={onFocus}
      position="relative"
    >
      <Input
        autoComplete="off"
        error={dateError || error}
        onBlur={handleBlur}
        onClick={openCalendar}
        onEnter={handleBlur}
        onValueChange={validateDate}
        rightAddon={rightAddon}
        value={dateInput}
        {...rest}
      />
      <DatePickerContainer
        ref={popperRef}
        show={showCalendar}
        style={styles.popper}
        {...attributes.popper}
      >
        <DatePicker
          endDate={value ? new Date(value) : undefined}
          isDateBlocked={isDateBlocked}
          minWidth="200px"
          onDateChange={dateChangeHandler}
          startDate={value ? new Date(value) : undefined}
        />
      </DatePickerContainer>
      {helper && <Helper error={error}>{helper}</Helper>}
    </Box>
  )
}
